/**
 * DesktopOnlyGate — честная заглушка для узких экранов (#322).
 *
 * Адаптив в Hub'е сделан только для обвязки: сайдбар превращается в Drawer, а
 * сами страницы под узкий экран не рассчитаны. Замер на 390×844: заголовок
 * «Analytics» переносится ПО ОДНОЙ БУКВЕ в столбик, поле поиска и карточка
 * контента уезжают за правый край, KPI-карточки дашборда обрезаны.
 *
 * Полноценная мобильная вёрстка здесь не решение: триаж находок, настройка
 * колонок и конструктор панелей на телефоне не работают в принципе — значит,
 * вложение в вёрстку дало бы экран, который выглядит рабочим и им не является.
 * Поэтому заглушка, но с тремя обязательными свойствами:
 *
 *   - **не запирает**: есть выход «всё равно открыть» — иногда нужно просто
 *     посмотреть, и запрещать это мы не вправе;
 *   - **помнит решение** в пределах вкладки (`sessionStorage`, не `localStorage`:
 *     решение «посмотреть с телефона один раз» не должно молча действовать
 *     через неделю на другом устройстве того же пользователя);
 *   - **отдаёт адрес**: главный сценарий — переслать ссылку себе на десктоп,
 *     поэтому адрес и виден текстом, и копируется кнопкой. Сбой буфера обмена
 *     (нет разрешения, небезопасный контекст) экран не роняет — адрес остаётся
 *     на месте, его можно выделить руками.
 *
 * Порог 1024, а не 992 из `MOBILE_BREAKPOINT`: тот отвечает за превращение
 * сайдбара в Drawer, и планшет в 1000px по нему уже «мобильный», хотя таблицы
 * на нём читаются. Пороги разные потому, что отвечают на разные вопросы.
 */
import React, { useCallback, useState } from 'react';
import { Button, Space, Typography } from 'antd';
import { CopyOutlined, DesktopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { DESKTOP_MIN_WIDTH } from '../constants/breakpoints';

const { Title, Paragraph, Text } = Typography;

export const DESKTOP_GATE_DISMISSED_KEY = 'desktop-gate-dismissed';

const readDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(DESKTOP_GATE_DISMISSED_KEY) === 'true';
  } catch {
    // Приватный режим и запрет хранилища: заглушка просто не запомнит выбор.
    return false;
  }
};

export function DesktopOnlyGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const { t } = useTranslation('common');
  const isNarrow = useMediaQuery(`(max-width: ${DESKTOP_MIN_WIDTH - 1}px)`);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [copied, setCopied] = useState(false);

  const dismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DESKTOP_GATE_DISMISSED_KEY, 'true');
    } catch {
      // Не запомнили — но выпустить пользователя обязаны.
    }
    setDismissed(true);
  }, []);

  const copyUrl = useCallback(() => {
    const url = window.location.href;
    void navigator.clipboard
      ?.writeText(url)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  }, []);

  if (!isNarrow || dismissed) {
    return <>{children}</>;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '32px 20px',
        gap: 12,
      }}
    >
      <DesktopOutlined aria-hidden="true" style={{ fontSize: 56, opacity: 0.45 }} />
      <Title level={3} style={{ margin: 0 }}>
        {t('desktopGate.title')}
      </Title>
      <Paragraph type="secondary" style={{ maxWidth: 420, margin: 0 }}>
        {t('desktopGate.description', { width: DESKTOP_MIN_WIDTH })}
      </Paragraph>

      <Text
        code
        style={{ maxWidth: '100%', overflowWrap: 'anywhere', fontSize: 12 }}
      >
        {window.location.href}
      </Text>

      <Space direction="vertical" size={8} style={{ marginTop: 8 }}>
        {/* aria-label явно: иконка AntD несёт своё `aria-label="copy"`, и без
            этого доступное имя кнопки склеивается в «copy Скопировать адрес». */}
        <Button
          type="primary"
          icon={<CopyOutlined aria-hidden="true" />}
          onClick={copyUrl}
          aria-label={t('desktopGate.copy')}
        >
          {t('desktopGate.copy')}
        </Button>
        {copied && <Text type="success">{t('desktopGate.copied')}</Text>}
        <Button type="text" onClick={dismiss}>
          {t('desktopGate.openAnyway')}
        </Button>
      </Space>
    </div>
  );
}

export default DesktopOnlyGate;
